<template>
  <div v-if="loading" class="skeleton-loader-wrapper">
    <SkeletonLoader :loadingText="loadingText" />
  </div>
  <div v-else>
    <div class="view user-data-view">
      <div class="ud-header">
        <h1 class="ud-title">Tell us about yourself</h1>
        <p class="ud-sub">Just the basics — takes about a minute.</p>
      </div>

      <form @submit.prevent="UpdateUserProfile">
        <!-- Your name -->
        <div class="ud-section">
          <p class="ud-section-label">Your name</p>
          <div class="ud-row two">
            <div class="ud-field">
              <label class="ud-label" for="firstname"
                >First Name <span class="ud-asterisk">*</span></label
              >
              <input
                id="firstname"
                required
                type="text"
                class="ud-input"
                v-model="formData.firstName"
                placeholder="Jane"
              />
            </div>
            <div class="ud-field">
              <label class="ud-label" for="lastname"
                >Last Name <span class="ud-asterisk">*</span></label
              >
              <input
                id="lastname"
                required
                type="text"
                class="ud-input"
                v-model="formData.lastName"
                placeholder="Smith"
              />
            </div>
          </div>
          <div class="ud-row two">
            <div class="ud-field">
              <label class="ud-label" for="middlename">Middle Name</label>
              <input
                id="middlename"
                type="text"
                class="ud-input"
                v-model="formData.middleName"
                placeholder="Optional"
              />
            </div>
            <div class="ud-field">
              <label class="ud-label" for="displayname">Display Name</label>
              <input
                id="displayname"
                type="text"
                class="ud-input"
                v-model="formData.displayName"
                placeholder="What should we call you?"
              />
            </div>
          </div>
        </div>

        <!-- About you -->
        <div class="ud-section">
          <p class="ud-section-label">About you</p>
          <div class="ud-row">
            <div class="ud-field">
              <label class="ud-label" for="dob"
                >Date of Birth <span class="ud-asterisk">*</span></label
              >
              <input
                id="dob"
                type="date"
                class="ud-input date-picker"
                required
                v-model="formData.dateOfBirth"
                placeholder="MM/DD/YYYY"
              />
            </div>
          </div>

          <div
            v-if="showJuniorWarning && formData.dateOfBirth != null"
            class="ud-junior-card"
          >
            <p>
              Looks like you're under 18. You'll need a junior account instead.
            </p>
            <button
              type="button"
              class="ud-junior-btn"
              @click="createJuniorAccount"
            >
              Switch to Junior Account
            </button>
          </div>

          <div class="ud-row two">
            <div class="ud-field">
              <label class="ud-label" for="gender">Gender</label>
              <select
                class="ud-input ud-select"
                id="gender"
                v-model="formData.gender"
              >
                <option value="" disabled>Select...</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Non-Binary">Non-Binary</option>
                <option value="Other">Other</option>
                <option value="Prefer not to answer">
                  Prefer not to answer
                </option>
              </select>
            </div>
            <div class="ud-field">
              <label class="ud-label" for="occupation">Occupation</label>
              <input
                id="occupation"
                type="text"
                class="ud-input"
                v-model="formData.occupation"
                placeholder="Start typing..."
                @input="filterOccupations"
                @blur="handleOccupationBlur"
              />
              <div
                v-if="filteredOccupations.length > 0"
                class="ud-autocomplete"
              >
                <div
                  v-for="(item, index) in filteredOccupations"
                  :key="index"
                  class="ud-autocomplete-item"
                  @click="selectOccupation(item)"
                >
                  {{ item }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Contact -->
        <div class="ud-section">
          <p class="ud-section-label">Contact</p>
          <div class="ud-row">
            <div class="ud-field">
              <label class="ud-label" for="email"
                >Email Address <span class="ud-asterisk">*</span></label
              >
              <input
                id="email"
                type="email"
                class="ud-input"
                v-model="formData.email"
                autocomplete="email"
                required
              />
            </div>
          </div>
          <div class="ud-row">
            <div class="ud-field">
              <label class="ud-label" for="phone">Phone Number</label>
              <input
                type="tel"
                id="phone"
                class="ud-input"
                v-model="formData.phoneNumber"
                autocomplete="tel"
                pattern="(\([0-9]{3}\)|[0-9]{3})-[0-9]{3}-[0-9]{4}"
                title="Enter a valid US phone number (e.g., 555-555-5555)"
                placeholder="555-555-5555"
                @input="formatPhoneNumber"
                maxlength="12"
              />
            </div>
          </div>
        </div>

        <!-- Address -->
        <div class="ud-section">
          <p class="ud-section-label">Address</p>
          <div class="ud-row">
            <div class="ud-field">
              <label class="ud-label" for="address">Street Address</label>
              <input
                type="text"
                class="ud-input"
                v-model="formData.address"
                id="address"
                autocomplete="street-address"
                placeholder="123 Main St"
              />
            </div>
          </div>
          <div class="ud-row two">
            <div class="ud-field">
              <label class="ud-label" for="city">City</label>
              <input
                type="text"
                class="ud-input"
                v-model="formData.city"
                id="city"
                autocomplete="address-level2"
              />
            </div>
            <div class="ud-field">
              <label class="ud-label" for="state">State</label>
              <input
                type="text"
                class="ud-input"
                v-model="formData.state"
                id="state"
                autocomplete="address-level1"
              />
            </div>
          </div>
          <div class="ud-row">
            <div class="ud-field">
              <label class="ud-label" for="zip">Zipcode</label>
              <input
                id="zip"
                type="text"
                class="ud-input"
                autocomplete="postal-code"
                v-model="formData.zipCode"
              />
            </div>
          </div>
        </div>

        <button
          role="button"
          type="submit"
          aria-label="submit update user profile"
          class="ud-submit"
          :disabled="
            !formData.dateOfBirth || formData.dateOfBirth === 0 || !isUserOver18
          "
        >
          Next →
        </button>
      </form>
    </div>

    <ProgressBar
      :currentStep="1"
      :steps="4"
      :stepLabels="['Account Type', 'Your Details', 'Categories', 'All Done']"
    />
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import SkeletonLoader from '../../components/SkeletonLoader.vue';
import { useMyStore } from '../../store';
import { Occupation_List } from '../../utils/occupationList';
import ProgressBar from '../../components/ProgressBar.vue';

export default {
  components: { SkeletonLoader, ProgressBar },
  setup() {
    const store = useMyStore();
    const formData = ref({
      firstName: '',
      displayName: '',
      middleName: '',
      lastName: '',
      dateOfBirth: null,
      gender: '',
      email: store.userInfo.email,
      phoneNumber: '',
      address: '',
      state: '',
      zipCode: '',
      city: '',
      occupation: '',
    });
    const router = useRouter();
    const loading = ref(true);
    let loadingText = ref('Saving your details...');
    const user = ref();
    const showJuniorWarning = ref(false);
    const isUserOver18 = ref(false);
    const occupations = Occupation_List;
    const occupationInput = ref('');
    const filteredOccupations = ref([]);

    const filterOccupations = () => {
      if (!formData.value.occupation) {
        filteredOccupations.value = [];
        return;
      }
      filteredOccupations.value = occupations
        .filter((occ) =>
          occ.toLowerCase().includes(formData.value.occupation.toLowerCase())
        )
        .slice(0, 10);
    };

    const selectOccupation = (item) => {
      formData.value.occupation = item;
      filteredOccupations.value = [];
    };

    const handleOccupationBlur = () => {
      setTimeout(() => {
        filteredOccupations.value = [];
      }, 200);
    };

    const formatPhoneNumber = (event) => {
      let digits = event.target.value.replace(/\D/g, '').slice(0, 10);
      if (digits.length <= 3) {
        formData.value.phoneNumber = digits;
      } else if (digits.length <= 6) {
        formData.value.phoneNumber = `${digits.slice(0, 3)}-${digits.slice(3)}`;
      } else {
        formData.value.phoneNumber = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
      }
    };

    const validPhoneNumber = (number) => {
      return number.length === 10;
    };

    const createJuniorAccount = () => {
      router.push({ name: 'JuniorUserData' });
    };

    const UpdateUserProfile = () => {
      loadingText.value = 'Saving your details...';
      loading.value = true;

      try {
        let userData = {
          FirstName: formData.value.firstName,
          MiddleName: formData.value.middleName,
          LastName: formData.value.lastName,
          PreferredName: formData.value.preferredName
            ? formData.value.preferredName
            : formData.value.firstName,
          DateOfBirth: formData.value.dateOfBirth
            ? new Date(formData.value.dateOfBirth + 'T00:00:00').toISOString()
            : null,
          Gender: formData.value.gender,
          Occupation: formData.value.occupation,
          Email: formData.value.email,
          Address: formData.value.address,
          City: formData.value.city,
          State: formData.value.state,
          ZipCode: formData.value.zipCode,
          PhoneNumber: formData.value.phoneNumber,
          newUserLoginFlowComplete: true,
        };
        store.saveUserAccount(userData).then((response) => {
          if (response.status === 200) {
            store.getUserAccountData().then(() => {
              loading.value = false;
              router.push({ name: 'UserCategoryData' });
            });
          }
        });
      } catch (error) {
        loading.value = false;
        console.error('An error occurred:', error);
      }
    };

    const checkUserAge = () => {
      const birthdate = formData.value.dateOfBirth;
      if (!birthdate || new Date(birthdate).getFullYear() <= 1) {
        isUserOver18.value = false;
        showJuniorWarning.value = false;
        return;
      }

      const birthDate = new Date(birthdate);
      const today = new Date();

      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      const dayDiff = today.getDate() - birthDate.getDate();

      if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        age--;
      }

      isUserOver18.value = age >= 18;
      showJuniorWarning.value = !isUserOver18.value;
    };

    watch(
      () => formData.value.dateOfBirth,
      () => {
        checkUserAge();
      }
    );

    onMounted(async () => {
      setTimeout(() => {
        const mainElement = document.querySelector('.main');
        if (mainElement) {
          mainElement.scrollTo(0, 0);
        }
      }, 100);
      user.value = store.user;
      loading.value = false;
    });

    return {
      formData,
      router,
      UpdateUserProfile,
      loading,
      loadingText,
      isUserOver18,
      createJuniorAccount,
      showJuniorWarning,
      checkUserAge,
      formatPhoneNumber,
      validPhoneNumber,
      occupations,
      occupationInput,
      filteredOccupations,
      filterOccupations,
      selectOccupation,
      handleOccupationBlur,
    };
  },
};
</script>

<style scoped>
.user-data-view {
  max-width: 480px;
  margin: 0 auto;
}

.ud-header {
  text-align: center;
  padding: 1.75rem 0 0.25rem;
  margin-bottom: 1.5rem;
}

.ud-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #004751;
  margin: 0 0 0.3rem;
  letter-spacing: -0.02em;
}

.dark-theme .ud-title {
  color: #5dcaa5;
}

.ud-sub {
  font-size: 0.85rem;
  color: #888;
  margin: 0;
}

.ud-section-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #7f77dd;
  margin: 0 0 0.75rem;
  padding-bottom: 0.4rem;
  border-bottom: 1.5px solid #eeedfe;
}

.dark-theme .ud-section-label {
  border-bottom-color: #2a2060;
}

.ud-row {
  display: grid;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.ud-row.two {
  grid-template-columns: 1fr 1fr;
}

.ud-autocomplete {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 10;
  background: white;
  border: 1.5px solid #e7e7e7;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  margin-top: 4px;
}

.dark-theme .ud-autocomplete {
  background: #212e3e;
  border-color: #2a3a4e;
}

.ud-autocomplete-item {
  padding: 10px 12px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background 0.15s;
  color: #213547;
}

.dark-theme .ud-autocomplete-item {
  color: #d4d2d2;
}

.ud-autocomplete-item:hover {
  background: #eeedfe;
}

.dark-theme .ud-autocomplete-item:hover {
  background: #2a3a4e;
}

.ud-junior-card {
  background: #eeedfe;
  border-radius: 12px;
  padding: 1rem 1.25rem;
  margin: 0.25rem 0 0.75rem;
  text-align: center;
}

.dark-theme .ud-junior-card {
  background: #1e1a40;
}

.ud-junior-card p {
  font-size: 0.82rem;
  color: #3c3489;
  margin: 0 0 0.75rem;
}

.dark-theme .ud-junior-card p {
  color: #afa9ec;
}

.ud-junior-btn {
  background: linear-gradient(135deg, #7f77dd, #5dcaa5);
  color: white;
  border: none;
  border-radius: 50px;
  padding: 0.55rem 1.25rem;
  font-family: 'Poppins', sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
}

.ud-junior-btn:hover {
  opacity: 0.9;
}
</style>

<!-- <template>
  <div v-if="loading" class="skeleton-loader-wrapper">
    <SkeletonLoader :loadingText="loadingText" />
  </div>
  <div v-else>
    <MainHeader :title="'Let\'s Get to Know You'"> </MainHeader>
    <div class="view user-data-view">
      <form @submit.prevent="UpdateUserProfile">
        <div>
          <label for="firstname"
            >First Name <span class="asterisk">*</span></label
          >
          <input
            id="firstname"
            required
            type="text"
            class="input"
            v-model="formData.firstName"
          />
        </div>
        <div class="mt-1">
          <label for="displayname">Display Name</label>
          <input
            id="displayname"
            type="text"
            class="input"
            v-model="formData.displayName"
          />
        </div>
        <div class="mt-1">
          <label for="middlename">Middle Name</label>
          <input
            id="middlename"
            type="text"
            class="input"
            v-model="formData.middleName"
          />
        </div>
        <div class="mt-1">
          <label for="lastname"
            >Last Name <span class="asterisk">*</span></label
          >
          <input
            id="lastname"
            required
            type="text"
            class="input"
            v-model="formData.lastName"
          />
        </div>
        <div class="mt-1">
          <label class="small-grey-text" for="dob"
            >Date of Birth <span class="asterisk">*</span></label
          >
          <input
            id="dob"
            type="date"
            class="input block date-picker"
            required
            v-model="formData.dateOfBirth"
            placeholder="MM/DD/YYYY"
          />
        </div>
        <div
          v-if="showJuniorWarning && formData.dateOfBirth != null"
          class="wrapper-card junior-card"
        >
          <p>
            It looks like you're too young for a standard account. Please create a
            junior account.
          </p>
          <button class="blue-button mt-1" @click="createJuniorAccount">
            Create Junior Account
          </button>
        </div>
        <div class="mt-1">
          <label for="gender">Gender</label>
          <select
            class="flex w-100 gender-select form-select"
            id="gender"
            v-model="formData.gender"
          >
            <option value="" disabled>Select a gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Non-Binary">Non-Binary</option>
            <option value="Other">Other</option>
            <option value="Prefer not to answer">Prefer not to answer</option>
          </select>
        </div>
        <div class="mt-1">
          <label for="occupation" class="small-grey-text">Occupation</label>
          <input
            id="occupation"
            type="text"
            class="input"
            v-model="formData.occupation"
            placeholder="Start typing your occupation..."
            @input="filterOccupations"
            @blur="handleOccupationBlur"
          />
          <div
            v-if="filteredOccupations.length > 0"
            class="autocomplete-list wrapper-card"
          >
            <div
              v-for="(item, index) in filteredOccupations"
              :key="index"
              class="autocomplete-item"
              @click="selectOccupation(item)"
            >
              {{ item }}
            </div>
          </div>
        </div>
        <div class="mt-1">
          <label class="small-grey-text" for="email"
            >Email Address <span class="asterisk">*</span></label
          >
          <input
            id="email"
            type="emailInput"
            class="input"
            v-model="formData.email"
            autocomplete="email"
            required
          />
        </div>
        <div class="mt-1">
          <label class="small-grey-text" for="phone">Phone Number</label>
          <input
            type="tel"
            id="phone"
            class="input"
            v-model="formData.phoneNumber"
            autocomplete="tel"
            pattern="(\([0-9]{3}\)|[0-9]{3})-[0-9]{3}-[0-9]{4}"
            title="Enter a valid US phone number (e.g., 555-555-5555)"
            placeholder="Enter a valid US phone number (e.g., 555-555-5555)"
            @input="formatPhoneNumber"
            maxlength="12"
          />
        </div>

        <div class="mt-1">
          <label class="small-grey-text" for="address">Address</label>
          <input
            type="text"
            class="input"
            v-model="formData.address"
            id="address"
            autocomplete="street-address"
          />
        </div>
        <div class="mt-1">
          <label class="small-grey-text" for="city">City</label>
          <input
            type="text"
            class="input"
            v-model="formData.city"
            id="city"
            autocomplete="address-level2"
          />
        </div>
        <div class="mt-1">
          <label class="small-grey-text" for="state">State</label>
          <input
            type="text"
            class="input"
            v-model="formData.state"
            id="state"
            autocomplete="address-level1"
          />
        </div>
        <div class="mt-1">
          <label class="small-grey-text" for="zip">Zipcode</label>
          <input
            id="zip"
            type="text"
            class="input"
            autocomplete="postal-code"
            v-model="formData.zipCode"
          />
        </div>
        <button
          role="button"
          type="submit"
          aria-label="submit update user profile"
          class="teal-button mt-4 w-100"
          :disabled="
            !formData.dateOfBirth || formData.dateOfBirth === 0 || !isUserOver18
          "
        >
          Next
        </button>
      </form>
    </div>
    <ProgressBar :currentStep="1" :steps="4" :stepLabels="['Account Type', 'Your Details', 'Categories', 'All Done']" />
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import MainHeader from '../../components/View-Header.vue';
import SkeletonLoader from '../../components/SkeletonLoader.vue';
import { useMyStore } from '../../store';
import { Occupation_List } from '../../utils/occupationList';
import ProgressBar from '../../components/ProgressBar.vue';

export default {
  components: { MainHeader, SkeletonLoader, ProgressBar },
  setup() {
    const store = useMyStore();
    const formData = ref({
      firstName: '',
      displayName: '',
      middleName: '',
      lastName: '',
      dateOfBirth: null,
      gender: '',
      email: store.userInfo.email,
      phoneNumber: '',
      address: '',
      state: '',
      zipCode: '',
      city: '',
      occupation: '',
    });
    const router = useRouter();
    const loading = ref(true);
    let loadingText = ref('Loading...');
    const user = ref();
    const showJuniorWarning = ref(false);
    const isUserOver18 = ref(false);
    const occupations = Occupation_List;
    const occupationInput = ref('');
    const filteredOccupations = ref([]);

    const filterOccupations = () => {
      if (!formData.value.occupation) {
        filteredOccupations.value = [];
        return;
      }
      filteredOccupations.value = occupations
        .filter((occ) =>
          occ.toLowerCase().includes(formData.value.occupation.toLowerCase())
        )
        .slice(0, 10);
    };

    const selectOccupation = (item) => {
      formData.value.occupation = item;
      filteredOccupations.value = [];
    };

    const handleOccupationBlur = () => {
      setTimeout(() => {
        filteredOccupations.value = [];
      }, 200);
    };

    const formatPhoneNumber = (event) => {
      let digits = event.target.value.replace(/\D/g, '').slice(0, 10);
      if (digits.length <= 3) {
        formData.value.phoneNumber = digits;
      } else if (digits.length <= 6) {
        formData.value.valuel.phoneNumber = `${digits.slice(0, 3)}-${digits.slice(3)}`;
      } else {
        formData.value.phoneNumber = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
      }
    };

    const validPhoneNumber = (number) => {
      return number.length === 10;
    };

    const enterUserData = () => {
      router.push({ name: 'UserCategoryData' });
    };

    const createJuniorAccount = () => {
      router.push({ name: 'JuniorUserData' });
    };

    const UpdateUserProfile = () => {
      loadingText.value = 'Saving Account...';
      loading.value = true;

      try {
        let userData = {
          FirstName: formData.value.firstName,
          MiddleName: formData.value.middleName,
          LastName: formData.value.lastName,
          PreferredName: formData.value.preferredName
            ? formData.value.preferredName
            : formData.value.firstName,
          DateOfBirth: formData.value.dateOfBirth
            ? new Date(formData.value.dateOfBirth + 'T00:00:00').toISOString()
            : null,
          Gender: formData.value.gender,
          Occupation: formData.value.occupation,
          Email: formData.value.email,
          Address: formData.value.address,
          City: formData.value.city,
          State: formData.value.state,
          ZipCode: formData.value.zipCode,
          PhoneNumber: formData.value.phoneNumber,
          newUserLoginFlowComplete: true,
        };
        store.saveUserAccount(userData).then((response) => {
          if (response.status === 200) {
            store.getUserAccountData().then(() => {
              loading.value = false;
              router.push({ name: 'UserCategoryData' });
            });
          }
        });
      } catch (error) {
        loading.value = false;
        console.error('An error occurred:', error);
      }
    };

    const checkUserAge = () => {
      const birthdate = formData.value.dateOfBirth;
      if (!birthdate || new Date(birthdate).getFullYear() <= 1) {
        isUserOver18.value = false;
        showJuniorWarning.value = false;
        return;
      }

      const birthDate = new Date(birthdate);
      const today = new Date();

      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      const dayDiff = today.getDate() - birthDate.getDate();

      if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        age--;
      }

      isUserOver18.value = age >= 18;
      showJuniorWarning.value = !isUserOver18.value;
    };

    watch(
      () => formData.value.dateOfBirth,
      () => {
        checkUserAge();
      }
    );

    onMounted(async () => {
      setTimeout(() => {
        const mainElement = document.querySelector('.main');
        if (mainElement) {
          mainElement.scrollTo(0, 0);
        }
      }, 100);

      user.value = store.user;
      loading.value = false;
    });

    return {
      formData,
      enterUserData,
      router,
      UpdateUserProfile,
      loading,
      loadingText,
      isUserOver18,
      createJuniorAccount,
      showJuniorWarning,
      checkUserAge,
      formatPhoneNumber,
      validPhoneNumber,
      occupations,
      occupationInput,
      filteredOccupations,
      filterOccupations,
      selectOccupation,
      handleOccupationBlur,
    };
  },
};
</script>
<style scoped>
.user-data-view .input {
  width: 100%;
  box-sizing: border-box;
  font-size: 1em;
}

.gender-select {
  box-sizing: border-box;
  font-size: 1em;
  border: 1px solid #e7e7e7;
  border-radius: 10px;
  padding: 10px;
}

.asterisk {
  color: #747373;
}

.junior-card {
  padding: 1em;
  text-align: center;
  color: #747373;
}

.gender-select {
  box-sizing: border-box;
  font-size: 1em;
  border: 1px solid #e7e7e7;
  border-radius: 10px;
  padding: 10px;
}

.autocomplete-list {
  padding-left: 1em;
}

.autocomplete-item {
  padding: 10px;
  cursor: pointer;
}

.autocomplete-item:hover {
  background: #f0f0f0;
}
</style> -->
